import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const RefundPolicyPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="py-6 px-6 border-b border-border/40">
        <div className="max-w-4xl mx-auto flex items-center">
          <Link to="/" className="flex items-center">
            <img src="/logo.svg" alt="CVForge Logo" className="w-10 h-10 mr-3" />
            <div>
              <h1 className="font-heading text-2xl font-semibold tracking-tight">CVForge</h1>
              <p className="text-xs text-muted-foreground">Professional CV Builder</p>
            </div>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Home
          </Link>

          <h1 className="font-heading text-3xl font-semibold mb-8">Refund & Cancellation Policy</h1>
          
          <div className="prose prose-neutral max-w-none space-y-6 text-muted-foreground">
            <p className="text-sm">
              <strong className="text-foreground">Last Updated:</strong> April 2026
            </p>

            <section className="space-y-3">
              <h2 className="font-heading text-xl font-medium text-foreground">1. Overview</h2>
              <p>
                At InstantCVForge, we strive to provide a high-quality CV building service. 
                This policy outlines our refund and cancellation terms for purchases made on our platform.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-xl font-medium text-foreground">2. Digital Product Nature</h2>
              <p>
                Our service provides instant access to digital products (downloadable CV PDFs). 
                Due to the immediate delivery and digital nature of our products, we have specific 
                refund guidelines outlined below.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-xl font-medium text-foreground">3. Refund Eligibility</h2>
              <p>You may be eligible for a refund in the following circumstances:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong className="text-foreground">Technical Issues:</strong> If you experience technical 
                  problems that prevent you from downloading your CV and our support team cannot resolve 
                  the issue within 48 hours.
                </li>
                <li>
                  <strong className="text-foreground">Duplicate Charges:</strong> If you were accidentally 
                  charged multiple times for the same CV.
                </li>
                <li>
                  <strong className="text-foreground">Service Not Delivered:</strong> If payment was processed 
                  but you did not receive access to your CV download.
                </li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-xl font-medium text-foreground">4. Non-Refundable Situations</h2>
              <p>Refunds will generally not be provided in the following cases:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You have successfully downloaded your CV PDF.</li>
                <li>Change of mind after completing the purchase and receiving the product.</li>
                <li>Dissatisfaction with the CV content you personally entered.</li>
                <li>Failure to use the service within 30 days of purchase.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-xl font-medium text-foreground">5. Cancellation Policy</h2>
              <p>
                Since our service provides instant digital delivery, cancellation is only possible 
                before completing the payment process. Once payment is confirmed and the CV is generated, 
                the transaction is considered final.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-xl font-medium text-foreground">6. How to Request a Refund</h2>
              <p>To request a refund, please:</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Email us at <a href="mailto:support@instantcvforge.com" className="text-primary hover:underline">support@instantcvforge.com</a></li>
                <li>Include your payment reference number</li>
                <li>Provide the email address used for the purchase</li>
                <li>Describe the reason for your refund request</li>
              </ol>
              <p>
                We aim to respond to all refund requests within 2-3 business days.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-xl font-medium text-foreground">7. Refund Processing</h2>
              <p>
                Approved refunds will be processed within 5-10 business days. The refund will be 
                credited to the original payment method used during purchase. Please note that your 
                bank or payment provider may take additional time to reflect the refund in your account.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-xl font-medium text-foreground">8. Contact Us</h2>
              <p>
                If you have any questions about this Refund & Cancellation Policy, please contact us:
              </p>
              <ul className="list-none space-y-1">
                <li><strong className="text-foreground">Email:</strong> <a href="mailto:support@instantcvforge.com" className="text-primary hover:underline">support@instantcvforge.com</a></li>
                <li><strong className="text-foreground">Website:</strong> instantcvforge.com</li>
              </ul>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border/40 mt-12">
        <div className="max-w-4xl mx-auto text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} InstantCVForge. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default RefundPolicyPage;
